import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { Container, Title, Text, Paper, Group, Badge, Box, Loader, Flex, Anchor, Breadcrumbs } from '@mantine/core';
import { CalendarBlank, User, ArrowLeft } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import api from '../utils/api';

const CATEGORY_COLORS = {
  'google-ads': 'blue',
  'optymalizacja': 'green',
  'ai': 'brand',
  'e-commerce': 'orange',
  'case-study': 'violet',
  'nowosci': 'cyan',
};

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function BlogPostPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/accounts/blog/${slug}/`)
      .then(res => setPost(res.data))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <>
        <Navbar />
        <Flex justify="center" align="center" h={400}><Loader color="brand" /></Flex>
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Navbar />
        <Container size="sm" py={80} ta="center">
          <Title order={3} mb="sm">{t('blog.notFoundTitle')}</Title>
          <Anchor component={Link} to="/blog">{t('blog.backToBlog')}</Anchor>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO title={post.title} description={post.excerpt} path={`/blog/${post.slug}`} />
      <Navbar />
      <Container size="sm" py={48}>
        <Breadcrumbs mb="md" styles={{ separator: { color: 'var(--mantine-color-gray-4)' } }}>
          <Anchor component={Link} to="/blog" size="sm">{t('blog.breadcrumb')}</Anchor>
          <Text size="sm" c="dark" truncate style={{ maxWidth: 300 }}>{post.title}</Text>
        </Breadcrumbs>

        <Badge size="sm" variant="light" color={CATEGORY_COLORS[post.category] || 'gray'} mb="sm" styles={{ root: { textTransform: 'none' } }}>
          {post.category_label}
        </Badge>

        <Title order={2} mb="sm" lh={1.3}>{post.title}</Title>

        <Group gap="md" mb="xl">
          <Group gap={4}>
            <User size={14} className="text-gray-400" />
            <Text size="sm" c="dimmed">{post.author}</Text>
          </Group>
          <Group gap={4}>
            <CalendarBlank size={14} className="text-gray-400" />
            <Text size="sm" c="dimmed">{formatDate(post.published_at)}</Text>
          </Group>
        </Group>

        {post.cover_image && (
          <Box
            mb="xl"
            style={{
              borderRadius: 12, overflow: 'hidden', maxHeight: 400,
              background: `url(${post.cover_image}) center/cover`,
              aspectRatio: '16/9',
            }}
          />
        )}

        <Paper
          withBorder p="xl" radius="md"
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: post.content }}
          style={{
            fontSize: 15,
            lineHeight: 1.8,
            color: 'var(--mantine-color-dark-7)',
            backgroundColor: 'white',
          }}
        />

        <Paper withBorder p="md" radius="md" mt="xl" style={{ background: 'var(--mantine-color-brand-0)' }}>
          <Text size="sm" fw={600} mb={4}>{t('blog.ctaTitle')}</Text>
          <Text size="sm" c="dimmed">
            {t('blog.ctaText')}{' '}
            <Anchor component={Link} to="/rejestracja" fw={500}>{t('blog.ctaButton')}</Anchor>
          </Text>
        </Paper>
      </Container>
      <Footer />
    </>
  );
}
